import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import { useApiError } from '../api/describe.ts';
import type { Session } from '../api/types.ts';
import { Button, Field, Notice, TextInput } from '../components/ui.tsx';
import { Brand, Preferences } from '../components/Shell.tsx';
import './SignIn.css';

/**
 * Sign in, or create an account.
 *
 * One screen for both, because a customer arriving from a sales page does not
 * know which of the two they need, and making them find out is the first
 * thing that loses them.
 */
export function SignIn({ onSignedIn }: { onSignedIn: (session: Session) => void }): ReactNode {
  const { t, locale } = useI18n();
  const describe = useApiError();
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const session =
        mode === 'signIn'
          ? await api.signIn(email, password)
          : await api.register({ email, password, displayName, organizationName, locale });
      onSignedIn(session);
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <header className="signin__bar">
        <Brand />
        <Preferences />
      </header>

      <main className="signin__main">
        <div className="signin__panel">
          <h1>{mode === 'signIn' ? t('auth.welcome') : t('auth.welcomeNew')}</h1>
          <p className="muted signin__tagline">{t('app.tagline')}</p>

          <form className="signin__form" onSubmit={(event) => void submit(event)}>
            {mode === 'signUp' ? (
              <>
                <Field label={t('auth.displayName')} required>
                  {(props) => (
                    <TextInput
                      {...props}
                      value={displayName}
                      autoComplete="name"
                      required
                      onChange={(event) => setDisplayName(event.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('auth.organizationName')} required>
                  {(props) => (
                    <TextInput
                      {...props}
                      value={organizationName}
                      autoComplete="organization"
                      required
                      onChange={(event) => setOrganizationName(event.target.value)}
                    />
                  )}
                </Field>
              </>
            ) : null}

            <Field label={t('auth.email')} required>
              {(props) => (
                <TextInput
                  {...props}
                  type="email"
                  // Latin script whichever language the interface is in.
                  className="ltr-text"
                  value={email}
                  autoComplete="email"
                  required
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </Field>

            <Field
              label={t('auth.password')}
              required
              help={mode === 'signUp' ? t('auth.passwordHint') : undefined}
            >
              {(props) => (
                <TextInput
                  {...props}
                  type="password"
                  className="ltr-text"
                  value={password}
                  autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
                  required
                  minLength={mode === 'signUp' ? 10 : undefined}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>

            {error ? <Notice tone="danger">{error}</Notice> : null}

            <Button type="submit" size="lg" busy={busy} className="signin__submit">
              {mode === 'signIn' ? t('auth.signIn') : t('auth.signUp')}
            </Button>
          </form>

          <p className="signin__switch">
            {mode === 'signIn' ? t('auth.needAccount') : t('auth.haveAccount')}{' '}
            <button
              type="button"
              className="linklike"
              onClick={() => {
                setMode(mode === 'signIn' ? 'signUp' : 'signIn');
                setError(undefined);
              }}
            >
              {mode === 'signIn' ? t('auth.signUp') : t('auth.signIn')}
            </button>
          </p>
        </div>

        <aside className="signin__aside" aria-hidden="true">
          <div className="signin__art" />
        </aside>
      </main>
    </div>
  );
}
